// src/components/sales/pages/clients/ClientsPage.jsx
import React, { useEffect, useState } from "react";
import ClientsView from "./ClientsView";
import { clientsService } from "../../../../services/clients.service";

export default function ClientsPage({ authData, logout }) {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [dataBlocks, setDataBlocks] = useState([]);

  // Message banner بدل alert
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });

  const setError = (text) => setUiMessage({ type: "error", text: text || "Request failed" });
  const setSuccess = (text) => setUiMessage({ type: "success", text: text || "Done" });
  const clearMessage = () => setUiMessage({ type: "", text: "" });

  const unwrapAnyArray = (res) => {
    if (Array.isArray(res)) return res;

    const d1 = res?.data ?? res?.result ?? res?.items ?? res;
    if (Array.isArray(d1)) return d1;

    if (Array.isArray(d1?.data)) return d1.data;
    if (Array.isArray(d1?.dataBlocks)) return d1.dataBlocks;

    return [];
  };

  const isForbidden = (e) => String(e?.message || "").toLowerCase().startsWith("http 403");
  const isNotFound = (e) => String(e?.message || "").toLowerCase().startsWith("http 404");

  const loadDataBlocks = async () => {
    const res = await clientsService.dataBlocks();
    const list = unwrapAnyArray(res);
    setDataBlocks(list);
    return list;
  };

  const loadClients = async () => {
    const res = await clientsService.list({});
    const list = unwrapAnyArray(res);
    setClients(list);
    return list;
  };

  const refreshAll = async () => {
    setLoading(true);
    clearMessage();
    try {
      await Promise.all([loadDataBlocks(), loadClients()]);
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }
      setError(e?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async (payload) => {
    setLoading(true);
    clearMessage();
    try {
      await clientsService.create(payload);
      await loadClients();
      setSuccess("Client created successfully.");
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      setError(e?.message || "Create failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateClient = async (id, payload) => {
    const n = Number(id);
    if (!Number.isFinite(n)) {
      setError("Invalid client id");
      return false;
    }

    setLoading(true);
    clearMessage();
    try {
      await clientsService.update(n, payload);
      await loadClients();
      setSuccess("Client updated successfully.");
      return true;
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return false;
      }
      setError(e?.message || "Update failed");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (id) => {
    const n = Number(id);
    if (!Number.isFinite(n)) {
      setError("Invalid client id");
      return;
    }

    const ok = window.confirm("Are you sure you want to delete this client?");
    if (!ok) return;

    setLoading(true);
    clearMessage();

    try {
      await clientsService.remove(n);
      await loadClients();
      setSuccess("Client deleted successfully.");
    } catch (e) {
      if (e?.message === "UNAUTHORIZED") {
        logout?.();
        return;
      }

      if (isForbidden(e)) {
        setError("HTTP 403: You don’t have permission to delete clients with the current role.");
      } else if (isNotFound(e)) {
        setError("HTTP 404: Client not found (it may have already been deleted).");
        await loadClients().catch(() => {});
      } else {
        setError(e?.message || "Delete failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ClientsView
      authData={authData}
      logout={logout}
      clients={clients}
      dataBlocks={dataBlocks}
      loading={loading}
      message={uiMessage}
      onClearMessage={clearMessage}
      onRefresh={refreshAll}
      onCreate={createClient}
      onUpdate={updateClient}
      onDelete={deleteClient}
    />
  );
}
